import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { PencilSimpleLine, Exam, Lightning, GameController, Clock, UsersThree, Target, Sparkle } from '@phosphor-icons/react';

const TYPE_ICON = {
  mcq: Exam,
  writing: PencilSimpleLine,
  scenario: Lightning,
  simulation: GameController,
  timed_task: Clock,
  work_style: UsersThree,
};

const TIER_META = {
  specialized: { label: 'SPECIALIZED', className: 'bg-primary text-primary-foreground', tip: 'Matches your proven domain expertise.' },
  aligned: { label: 'IN YOUR FAMILY', className: 'bg-foreground text-background', tip: 'Tagged to a role family you picked.' },
  generic: { label: 'UNIVERSAL', className: 'bg-secondary text-foreground', tip: 'A general test that applies to every role.' },
  other: { label: 'OTHER', className: 'bg-muted text-muted-foreground', tip: 'Outside your current interests — browse if curious.' },
};

const AssessmentList = () => {
  const [items, setItems] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [families, setFamilies] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterFamily, setFilterFamily] = useState('auto');  // 'auto' | 'all' | <rf_id>

  useEffect(() => {
    Promise.all([
      api.get('/assessments'),
      api.get('/assessments/attempts/mine'),
      api.get('/role-families'),
      api.get('/candidates/me').catch(() => ({ data: null })),
    ])
      .then(([a, t, rf, p]) => {
        setItems(a.data);
        setAttempts(t.data);
        setFamilies(rf.data || []);
        setProfile(p.data || null);
      })
      .finally(() => setLoading(false));
  }, []);

  const bestScoreFor = (aid) => {
    const mine = attempts.filter((x) => x.assessment_id === aid && x.status === 'submitted');
    if (!mine.length) return null;
    return Math.max(...mine.map((a) => a.raw_score || 0));
  };

  // Priority: explicit profile.role_family_ids > inferred from attempts > show all
  const myFamilyIds = useMemo(() => {
    const ids = new Set();
    if (profile?.role_family_ids?.length) {
      profile.role_family_ids.forEach((id) => ids.add(id));
      return ids;
    }
    attempts.forEach((a) => { if (a.role_family_id) ids.add(a.role_family_id); });
    return ids;
  }, [attempts, profile]);

  // Backend already returns items sorted by match_tier when the caller is a candidate.
  // We only need to respect the family filter chips on top.
  const visibleFamilyIds = useMemo(() => {
    if (filterFamily === 'all') return null;
    if (filterFamily === 'auto') {
      // In auto mode we show specialized + aligned + generic (not 'other').
      return 'auto';
    }
    return new Set([filterFamily]);
  }, [filterFamily]);

  const filteredItems = useMemo(() => {
    if (!visibleFamilyIds) return items;
    if (visibleFamilyIds === 'auto') {
      // If competency signals exist, trust match_tier. Otherwise fall back to family filter.
      const hasTiering = items.some((a) => a.match_tier && a.match_tier !== 'other');
      if (hasTiering) {
        return items.filter((a) => a.match_tier && a.match_tier !== 'other');
      }
      if (myFamilyIds.size > 0) return items.filter((a) => myFamilyIds.has(a.role_family_id));
      return items;
    }
    return items.filter((a) => visibleFamilyIds.has(a.role_family_id));
  }, [items, visibleFamilyIds, myFamilyIds]);

  const familiesWithAssessments = useMemo(() => {
    const ids = new Set(items.map((a) => a.role_family_id));
    return families.filter((f) => ids.has(f.id));
  }, [items, families]);

  const signals = profile?.competency_signals?.domain_expertise || [];
  const isAutoFiltered = filterFamily === 'auto';
  const hasProfileInterests = (profile?.role_family_ids?.length || 0) > 0;
  const hasSignals = signals.length > 0;

  return (
    <Shell>
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">ASSESSMENTS</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Prove your capability.</h1>
        <p className="text-sm text-muted-foreground mb-6">
          {isAutoFiltered
            ? hasSignals
              ? 'Personalized to your proven expertise. Specialized matches first, then universal tests. Toggle below to browse others.'
              : (hasProfileInterests
                ? "Showing tests for the families you picked on your Profile. Toggle below to browse others."
                : "Showing tests for the role families you've already worked in. Toggle below to browse others.")
            : 'Pick a role family or browse all tests. '}
          {!hasProfileInterests && !hasSignals && (
            <Link to="/candidate/profile" className="text-primary underline" data-testid="profile-interests-link">
              Set your target role families →
            </Link>
          )}
        </p>

        {hasSignals && isAutoFiltered && (
          <div className="border border-border bg-white p-4 mb-6 flex items-start gap-3" data-testid="signals-banner">
            <Sparkle size={18} weight="fill" className="text-primary shrink-0 mt-0.5" />
            <div>
              <div className="label-eyebrow text-muted-foreground mb-1">WE DETECTED YOU'RE STRONG IN</div>
              <div className="flex flex-wrap gap-1">
                {signals.map((s) => (
                  <span key={s} className="label-eyebrow bg-primary/10 text-primary px-2 py-0.5">{s}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Family filter chips */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="family-filter">
          <Button
            size="sm"
            variant={filterFamily === 'auto' ? 'default' : 'outline'}
            className="rounded-none"
            onClick={() => setFilterFamily('auto')}
            data-testid="filter-auto"
          >
            <Target size={14} className="mr-1" /> For me
          </Button>
          <Button
            size="sm"
            variant={filterFamily === 'all' ? 'default' : 'outline'}
            className="rounded-none"
            onClick={() => setFilterFamily('all')}
            data-testid="filter-all"
          >
            All ({items.length})
          </Button>
          {familiesWithAssessments.map((f) => {
            const count = items.filter((a) => a.role_family_id === f.id).length;
            const active = filterFamily === f.id;
            return (
              <Button
                key={f.id}
                size="sm"
                variant={active ? 'default' : 'outline'}
                className="rounded-none"
                onClick={() => setFilterFamily(f.id)}
                data-testid={`filter-${f.slug || f.id}`}
              >
                {f.name} ({count})
              </Button>
            );
          })}
        </div>

        {loading ? (
          <div className="mono text-sm text-muted-foreground">Loading...</div>
        ) : filteredItems.length === 0 ? (
          <div className="border border-border bg-white p-10 text-center" data-testid="empty-state">
            <Exam size={32} weight="duotone" className="mx-auto text-muted-foreground mb-2" />
            <div className="label-eyebrow text-muted-foreground mb-2">NO TESTS IN THIS FAMILY YET</div>
            <p className="text-sm text-muted-foreground mb-4">
              More assessments are being added. Try another family — or browse <button onClick={() => setFilterFamily('all')} className="text-primary underline">all tests</button>.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-0 grid-skeleton">
            {filteredItems.map((a) => {
              const Icon = TYPE_ICON[a.type] || Exam;
              const best = bestScoreFor(a.id);
              const family = families.find((f) => f.id === a.role_family_id);
              const tier = a.match_tier && TIER_META[a.match_tier];
              return (
                <div key={a.id} className="p-6 bg-white" data-testid={`assessment-card-${a.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <Icon size={24} weight="duotone" className="text-primary" />
                    <div className="flex flex-col items-end gap-1">
                      {tier && (
                        <span
                          className={`label-eyebrow px-2 py-0.5 ${tier.className}`}
                          title={tier.tip}
                          data-testid={`tier-${a.match_tier}-${a.id}`}
                        >
                          {tier.label}
                        </span>
                      )}
                      <span className="label-eyebrow text-muted-foreground">{a.type}</span>
                    </div>
                  </div>
                  {family && (
                    <div className="label-eyebrow text-primary/70 mb-1">{family.name}</div>
                  )}
                  <h3 className="font-display font-black text-xl tracking-tight mb-2">{a.title}</h3>
                  <div className="mono text-xs text-muted-foreground mb-3">
                    {a.items.length} items · {Math.round(a.time_limit_sec / 60)} min
                  </div>
                  {(a.specialization_tags?.length || 0) > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {a.specialization_tags.slice(0, 4).map((t) => (
                        <span key={t} className="label-eyebrow bg-secondary/70 text-foreground/70 px-2 py-0.5">{t}</span>
                      ))}
                    </div>
                  )}
                  {best !== null && (
                    <div className="mb-3 flex items-center gap-2">
                      <span className="label-eyebrow text-muted-foreground">BEST SCORE</span>
                      <span className="mono font-bold text-lg">{best}</span>
                    </div>
                  )}
                  <Link to={`/candidate/assessments/${a.id}`}>
                    <Button className="rounded-none w-full" data-testid={`assessment-start-${a.id}`}>
                      {best !== null ? 'Retake' : 'Start'}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
};

export default AssessmentList;
